import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  zodSelectBoxType,
  zodSelectBoxDefault,
} from "../../../../../../util/zod.util";
import { findNonEmptyDuplicateIndexes } from "../../../../../../util/form.utils";
import { useFormWrapper, useStoreState } from "../../../../../../util/util";
import { SelectBoxV2 } from "../../../../../../components/SelectBoxV2/SelectBoxV2";
import { Form } from "../../../../../../layout/FormLayout";
import { SelectWithDeleteButton } from "../../../../../../layout/shared";
import type { OptionType } from "../../../../../../types/types";
import type {
  AttributeSchema,
  AttributeGroupSummary,
  TemplateProductFormStepSchema,
  TemplateProductFormStepItemSchema,
  AttributeInput,
} from "../../../../../../types/types.PIM";
import { shouldShowObjectReferenceAttribute } from "../../../../../../components/ObjectReference/utils";
import {
  DeleteButton,
  PrimaryButtonLargeV2,
  SecondaryButtonWithPlusIcon,
  TertiaryButtonLargeV2,
} from "../../../../../../components/Buttons/Buttons";
import styled from "styled-components";
import { spacingTokens } from "../../../../../../designTokens";

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  button {
    display: flex;
  }
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-top: ${({ theme }) => theme.resolve("spacing_4xl")};
`;

const AddAttributeToTemplateSchema = z.object({
  attributes: z
    .object({
      attribute: zodSelectBoxType,
    })
    .array()
    .optional(),
});

const AddAttributeToTemplateFormSchemaFn = (t: (s: string) => string) =>
  z
    .object({
      attributes: z
        .object({
          attribute: zodSelectBoxDefault(t),
        })
        .array()
        .optional(),
    })
    .superRefine(({ attributes }, ctx) => {
      const duplicateIdxs = attributes
        ? findNonEmptyDuplicateIndexes([
            ...attributes.map(({ attribute: { value } }) => value),
          ])
        : [];
      if (duplicateIdxs.length > 0) {
        duplicateIdxs.forEach((idx) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Duplicate attributes not allowed"),
            path: [`attributes[${idx - 1}].attribute`],
          });
        });
      }
    });

type formOutput = z.infer<typeof AddAttributeToTemplateSchema>;

export const attributeToOption = (
  attribute: AttributeSchema
): OptionType<string> => ({
  value: attribute.id,
  label: attribute.name,
});

export function AttributesForm({
  step,
  onSuccess,
  templateGroups,
  onCancel,
}: {
  onSuccess: (
    step: TemplateProductFormStepSchema,
    items: TemplateProductFormStepItemSchema[]
  ) => void;
  templateGroups: AttributeGroupSummary[];
  step: TemplateProductFormStepSchema;
  onCancel: () => void;
}) {
  const [attributeOptions, setAttributeOptions] = useState<
    OptionType<string>[]
  >([]);
  const { t } = useTranslation();
  const {
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const templategroupAttributes = templateGroups
    .map((group) => group.attributes)
    .flat();
  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddAttributeToTemplateFormSchemaFn(t)),
    defaultValues: {
      attributes: [{ attribute: { label: undefined, value: undefined } }],
    },
  });
  const { handleSubmit, control, formState, errors } = methodsOfUseForm;

  const { fields, remove, append } = useFieldArray({
    control,
    name: "attributes",
  });

  const createFields = (): JSX.Element[] => {
    return fields.map((field, index: number) => (
      <SelectWithDeleteButton key={field.id}>
        <Controller
          key={field.id}
          as={SelectBoxV2}
          control={control}
          isSearchBar
          name={`attributes[${index}].attribute`}
          defaultValue={{ label: "", value: "" }}
          placeholder={t("Attribute Name")}
          options={attributeOptions.filter(
            (option) =>
              !step.items?.some((item) =>
                item.attributes.some(
                  (attribute) => attribute.attribute_id === option.value
                )
              )
          )}
          rules={{ required: true }}
          errors={{
            [`attributes[${index}].attribute`]:
              errors?.attributes?.[index]?.attribute?.value ??
              errors?.attributes?.[index]?.attribute ??
              undefined,
          }}
          formState={formState}
        />
        {index > 0 && (
          <DeleteButton
            testid={`delete-button-${index}`}
            onClick={() => remove(index)}
            type="button"
            height={20}
            width={20}
          />
        )}
      </SelectWithDeleteButton>
    ));
  };

  const onSubmit = async (values: formOutput) => {
    const newItems: TemplateProductFormStepItemSchema[] = [];
    values.attributes?.forEach((attribute) => {
      const attr = templategroupAttributes.find(
        (attr) => attribute.attribute.value === attr.id
      );
      const group = templateGroups.find((group) =>
        group.attributes.some((attr) => attr.id === attribute.attribute.value)
      );
      newItems.push({
        type: "group_attribute",
        id: crypto.randomUUID(),
        group_or_collection: group?.name ?? "",
        group_or_collection_id: group?.id ?? "",
        restrict_items_count: false,
        item_count: 0,
        attributes: [
          {
            attribute_id: attr?.id ?? "",
            display_name: attr?.display_name ?? "",
            name: attr?.name ?? "",
            id: attr?.id ?? "",
            is_required: attr?.is_required ?? false,
            input_type: attr?.input_type ?? ("form_field" as AttributeInput),
            choices: attr?.choices ?? [],
            is_multiple_choice: attr?.is_multiple_choice ?? false,
            priority: newItems.length ?? 1,
          },
        ],
      });
    });
    onSuccess(step, step.items ? [...step.items, ...newItems] : newItems);
  };

  useEffect(() => {
    setAttributeOptions(
      templateGroups
        .map((group) =>
          group.attributes
            .filter(
              (attribute) =>
                attribute.object_type !== "product_name" &&
                attribute.object_type !== "product_id" &&
                shouldShowObjectReferenceAttribute({
                  inputType: attribute.input_type,
                  objectReferenceEnabled: object_reference_enabled,
                })
            )
            .map((attribute) => ({
              label: `${group.name} / ${
                attribute.display_name ?? attribute.name
              }`,
              value: attribute.id,
            }))
        )
        .flat()
    );
  }, [templateGroups, object_reference_enabled]);

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        {attributeOptions && (
          <>
            {createFields()}
            <SecondaryButtonWithPlusIcon
              type="button"
              style={{ marginBottom: spacingTokens.spacing_5xl }}
              onClick={() =>
                append({ attribute: { label: undefined, value: undefined } })
              }
            >
              {t("Add")}
            </SecondaryButtonWithPlusIcon>
          </>
        )}
        <ButtonGroup>
          <TertiaryButtonLargeV2 fullWidth type="button" onClick={onCancel}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 fullWidth type="submit">
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonGroup>
      </Form>
    </>
  );
}
